
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createDesignation, updateDesignation } from '../../../../../modules/masterdata/actions';
import { IDesignation } from '../../../../../modules/masterdata/types';
import { selectCreateDesignationSuccess, selectUpdateDesignationSuccess } from '../../../../../modules/masterdata/selectors';

interface DesignationModalProps {
  open: boolean;
  onClose: () => void;
  designation?: IDesignation | null; // If provided, we are in Edit mode
}

const DesignationModal: React.FC<DesignationModalProps> = ({ open, onClose, designation }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isCreateSuccess = useSelector(selectCreateDesignationSuccess);
  const isUpdateSuccess = useSelector(selectUpdateDesignationSuccess);

  useEffect(() => {
    if (designation) {
      setName(designation.designation_name);
      setDescription(designation.designation_description);
    } else {
      setName('');
      setDescription('');
    }
  }, [designation, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (designation) {
      dispatch(updateDesignation({
        id: designation.designation_id,
        designation_name: name,
        designation_description: description
      }));
    } else {
      dispatch(createDesignation({
        designation_name: name,
        designation_description: description
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {designation ? 'Edit Designation' : 'Create Designation'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {designation ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DesignationModal;
